import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Lock, ImageOff } from "lucide-react";

import { VISIBILITY, VISIBILITY_FIELDS, readVisibility, editableFields, initials } from "@/lib/v3Pass";
import { useMemberAuth } from "../MemberAuthContext";
import { useMemberProfile } from "../useMemberProfile";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Avatar } from "../components/kit";
import { V3Title, V3Columns, V3Actions, AsideCard, StatusBanner, DetailRow, DetailList, ReadingPreview, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { AreaCard, ResponsibleCard } from "../components/v3app";
import { SelectCard } from "../components/v3pass";

function VisibilityForm({ p }) {
  const prof = p.data.profile;
  const vis = readVisibility(prof);
  const supported = vis.supported && editableFields(prof).includes("prefs");
  const [values, setValues] = useState(vis.values);
  const save = () => p.savePrefs({ ...p.prefs, profile_visibility: values }, "Sichtbarkeit gespeichert.");
  return (
    <div data-testid="profile-visibility-view" data-m="M82">
      <V3Title title="Profilfeld-Sichtbarkeit bearbeiten" intro="Lege die Sichtbarkeit deiner Profilfelder einzeln fest." testid="profile-visibility-title" />
      <V3Columns layout="settings" aside={<>
        <AsideCard icon={Eye} title="Deine Lesevorschau" text="Eine gut lesbare Ansicht unterstützt deinen eigenen nächsten Schritt." />
        <ReadingPreview large={p.a11y.large_text} contrast={p.a11y.high_contrast}
          action={<Link to="/profil/foto" data-testid="profile-visibility-next" className={`${V3_PRIMARY} lg:w-full`}>Nächsten Schritt ansehen</Link>} />
      </>} main={<>
        <div className="space-y-[12px]">
          {VISIBILITY_FIELDS.map(([k, label]) => <SelectCard key={k} id={`visibility-${k}`} title={label} value={values[k]} options={VISIBILITY}
            onChange={(v) => setValues((x) => ({ ...x, [k]: v }))} testid={`visibility-${k}`} />)}
        </div>
        {!supported && <div className="mt-[17px]"><StatusBanner tone="notice" icon={Lock} title="Speichern noch nicht verfügbar" testid="profile-visibility-blocked"
          text="Der gemeinsame Core kennt noch keine Sichtbarkeit je Profilfeld. Derzeit sehen alle angemeldeten Mitglieder Name, Stadt, Interessen und Kurzbeschreibung. Deine Auswahl wird nicht übernommen." /></div>}
        <V3Actions>
          <button type="button" onClick={save} disabled={!supported || p.saving} data-testid="profile-visibility-save" className={V3_PRIMARY}>{p.saving ? "Wird gespeichert …" : "Sichtbarkeit speichern"}</button>
          <Link to="/profil" data-testid="profile-visibility-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </>} />
    </div>
  );
}

// M82: one visibility per field (contract C.2 prefs.profile_visibility); saving only when the Core reports the field.
export function ProfileVisibility() {
  const p = useMemberProfile();
  if (p.error) return <ConnectionError onRetry={p.load} />;
  if (!p.data) return <Loading />;
  return <VisibilityForm key={JSON.stringify(readVisibility(p.data.profile))} p={p} />;
}

// M83: photo is optional; without a media endpoint (C.2) no file is picked or uploaded and initials stay.
export function ProfilePhoto() {
  const { member } = useMemberAuth();
  const ini = initials(member?.name);
  const current = ini ? `Initialen ${ini}` : "Initialen";
  return (
    <div data-testid="profile-photo-view" data-m="M83">
      <V3Title title="Profilfoto ändern" intro="Ein Profilfoto ist freiwillig. Initialen bleiben eine gleichwertige Alternative." testid="profile-photo-title" />
      <V3Columns layout="detail" aside={<><AreaCard title="Dein Bereich" text={current} testid="profile-photo-area" /><ResponsibleCard /></>} main={<>
        <div className="mb-[24px] flex items-center gap-[22px] lg:pl-[20px]">
          <Avatar name={member?.name} color={member?.avatar_color} size="h-[100px] w-[100px] text-[30px]" />
          <div><p className="text-[21px] font-bold leading-[28px] text-v3-ink">Foto freiwillig</p>
            <p className="mt-[12px] text-[14px] leading-[20px] text-v3-muted">Initialen bleiben als Alternative möglich.</p></div>
        </div>
        <DetailList>
          <DetailRow label="Aktuelles Profilbild" value={current} testid="profile-photo-current" />
          <DetailRow label="Eigenes Foto" value="Datei bewusst auswählen" testid="profile-photo-own" />
          <DetailRow label="Vorschau" value="Vor dem Speichern prüfen" testid="profile-photo-preview" />
          <DetailRow label="Foto entfernen" value="Danach wieder Initialen anzeigen" testid="profile-photo-remove" />
        </DetailList>
        <div className="mt-[17px]" id="profile-photo-blocked-hint"><StatusBanner tone="notice" icon={ImageOff} title="Fotos hochladen ist noch nicht verfügbar" testid="profile-photo-blocked"
          text="Der gemeinsame Core kann noch keine Profilfotos speichern. Es wird keine Datei ausgewählt oder übertragen; deine Initialen bleiben sichtbar." /></div>
        <V3Actions>
          <button type="button" disabled aria-describedby="profile-photo-blocked-hint" data-testid="profile-photo-select" className={V3_PRIMARY}>Foto auswählen</button>
          <Link to="/profil" data-testid="profile-photo-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </>} />
    </div>
  );
}
